// Must stay the first import: it strips the invite token from the URL before any
// analytics tool (loaded by the imports below) reads the page address.
import "./utils/stashInviteToken";

import { App as AntApp, ConfigProvider, type ThemeConfig } from "antd";
import { App } from "app/App";
import ReactDOM from "react-dom/client";
import { initCommandBar } from "utils/commandbar";
import { config } from "utils/config";
import { initFirebaseAuthListeners } from "utils/firebase";
import { sentry } from "utils/sentry";

import "styles/index.css";
import "styles/tailwind.css";
import flagsmith from "flagsmith";
import { FlagsmithProvider } from "flagsmith/react";
import { initHeap } from "./utils/heap";
import { initPostHog } from "./utils/posthog";

const theme: ThemeConfig = {
	hashed: false,
	cssVar: true,
	token: {
		// Editorial palette. MUST be literal hex (NOT var(--color-*)): AntD derives its
		// whole neutral palette (colorBgContainer, fills, hovers) by doing color MATH on
		// these values, which fails on a var() string. Keep these in sync with the same
		// hex in styles/index.css :root (AntD emits --ant-* vars from them via cssVar).
		colorLink: "#0f6b43",
		colorPrimary: "#0f6b43",
		colorSuccess: "#2e7d52",
		colorError: "#c0392b",
		colorInfo: "#0b73ea",
		colorWarning: "#f59e0b",
		borderRadius: 8,
		controlHeight: 40,
		colorBgBase: "#ffffff",
		colorText: "#14241c",
		colorTextSecondary: "#5b6b62",
		colorTextTertiary: "#8a978f",
		colorBorder: "#e4e8e1",
		colorBorderSecondary: "#eef1ec",
		fontFamily:
			"'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
		boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
		boxShadowSecondary:
			"0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04)",
	},
	components: {
		Button: {
			defaultShadow: "0 1px 2px rgba(0,0,0,0.05)",
			primaryShadow: "0 1px 2px rgba(0,0,0,0.05)",
			fontWeight: 600,
		},
		Card: {
			boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.03)",
			borderRadius: 8,
		},
		Layout: {
			bodyBg: "#f7f8f5",
			headerBg: "#ffffff",
			footerBg: "transparent",
			siderBg: "#f0f2ec",
			triggerBg: "transparent",
			triggerColor: "#0f6b43",
			headerHeight: 48,
		},
		Menu: {
			itemSelectedBg: "transparent",
			subMenuItemBg: "transparent",
			collapsedIconSize: 14,
			iconSize: 14,
			itemHeight: 36,
		},
		Tooltip: {
			controlHeight: 32,
		},
		Select: {
			optionSelectedColor: "#0f6b43",
			optionSelectedBg: "#e7f0ea",
		},
		Tabs: {},
		Table: {
			rowSelectedBg: "#e7f0ea",
			rowSelectedHoverBg: "#c3d6cc",
			headerBg: "#f0f2ec",
			headerColor: "#5b6b62",
		},
		Steps: {
			descriptionMaxWidth: 260,
		},
		Badge: {
			colorBgContainer: "#FFFFFF",
		},
	},
};

interface FlagsmithWrapperProps {
	children: React.ReactElement;
}

const defaultFlags = {
	disable_chrome_plugin: {
		id: 1,
		enabled: false,
		value: null,
	},
	hide_shopify: {
		id: 2,
		enabled: false,
		value: null,
	},
	// Kill switch for the Amazon integration card. Defaults ON (card disabled),
	// unlike hide_shopify, because Amazon is not usable yet: the SP-API
	// application registration has not been granted, so connecting cannot
	// succeed in any environment. Defaulting to hidden means the card stays
	// disabled before the flag exists remotely and if Flagsmith is unreachable.
	// Flip to enabled:false in the Flagsmith dashboard to expose it.
	hide_amazon: {
		id: 4,
		enabled: true,
		value: null,
	},
	// Kill switch for the reconciliation anomaly / JE-risk surface. Defaults ON
	// (feature hidden) so the surface is disabled for now even before the flag is
	// created remotely / if Flagsmith is unreachable. Flip to enabled:false in the
	// Flagsmith dashboard to bring it back.
	disable_anomaly_detection: {
		id: 3,
		enabled: true,
		value: null,
	},
	// The only switch for the workpaper UI (Workpaper tab, promote, close-page
	// aging report, PDF export, "Run complete" relabel). Defaults ON (hidden) so
	// the surface stays dark before the flag exists remotely and whenever
	// Flagsmith is unreachable. It hides web surfaces only: the workpaper API,
	// chat actions and MCP tools are never gated by it. Flip to enabled:false in
	// the Flagsmith dashboard to show the workpaper UI.
	hide_workpapers: {
		id: 5,
		enabled: true,
		value: null,
	},
	// Release switch for the Close Tracker inside Project Management: one tab
	// bar on /workflow (Projects, Tasks, Close board, Templates, Workflows) and
	// one sidebar group. Defaults ON (hidden), so the separate Close Tracker
	// page and sidebar group stay exactly as they are before the flag exists
	// remotely and whenever Flagsmith is unreachable. Flip to
	// enabled:false in the Flagsmith dashboard to show the tabs.
	hide_pm_close_tabs: {
		id: 6,
		enabled: true,
		value: null,
	},
};

function FlagsmithWrapper({ children }: FlagsmithWrapperProps) {
	// No `initialized` gate and no manual `flagsmith.init()` here, deliberately.
	//
	// `FlagsmithProvider` already calls `flagsmith.init(options)` itself on its
	// first render when `options` is supplied, so the old `useEffect` init was a
	// second, redundant round trip to flagsmith.equilityhq.com. Worse, the gate it
	// fed (`if (!initialized) return <div />`) blocked the ENTIRE app behind that
	// third-party request: every user stared at an empty div until it settled.
	//
	// `defaultFlags` below exists precisely so the app can render without waiting.
	// Flags resolve asynchronously and `useFlags` re-renders consumers when real
	// values land. The defaults are chosen to hide gated features, so the brief
	// pre-resolve window shows fewer features rather than leaking one early.

	return (
		<FlagsmithProvider
			flagsmith={flagsmith}
			options={{
				environmentID: process.env.REACT_APP_FLAGSMITH_KEY,
				defaultFlags,
				cacheFlags: true,
				cacheOptions: {
					ttl: 60 * 1000, // one minute
				},
				api: "https://flagsmith.equilityhq.com/api/v1/",
				onError: (error) => {
					// Non-fatal: `defaultFlags` keep the app usable when Flagsmith
					// is unreachable. Logged, not thrown.
					console.warn("Flagsmith error, using default flags:", error);
				},
			}}
		>
			{children}
		</FlagsmithProvider>
	);
}

if (!config.isLocal) {
	sentry.init(config.sentry.dsn);
	initHeap();
	initPostHog();
	initCommandBar();
}

// Register firebase -> auth-store sync listeners now that the full module graph
// is initialized (deferred out of firebase.ts to avoid a circular-import TDZ).
initFirebaseAuthListeners();

const root = ReactDOM.createRoot(
	document.getElementById("root") as HTMLElement,
);

if (window.self !== window.top) root.render(<div />);

root.render(
	<ConfigProvider theme={theme}>
		<AntApp>
			<FlagsmithWrapper>
				<App />
			</FlagsmithWrapper>
		</AntApp>
	</ConfigProvider>,
);
